// Roles & Permissions configuration.
//
// API endpoints below follow the existing master-data conventions (e.g. "Brand/search", "Brand",
// "Brand/{id}") and MUST be confirmed with the backend before release.
export const ROLE_API = {
  search: "Role/search", // POST { page, pageSize, filter, sort }
  base: "Role", // GET /Role/{id}, POST /Role, PUT /Role/{id}, DELETE /Role/{id}
};

// Operation keys match IPermission in commonFunctions/PermissionManger.tsx so getPermissions()
// can read a role's permissions directly once they are stored at login.
export type PermissionOperation = "view" | "add" | "edit" | "remove" | "exports";

export const PERMISSION_ACTIONS: { key: PermissionOperation; label: string }[] = [
  { key: "view", label: "View" },
  { key: "add", label: "Add" },
  { key: "edit", label: "Edit" },
  { key: "remove", label: "Delete" },
  { key: "exports", label: "Export" },
];

export interface PermissionPage {
  key: string; // page key passed to getPermissions()
  label: string;
  path: string; // list route; used to filter the sidebar menu
  actions: PermissionOperation[]; // actions that apply to this page
  hint?: string; // clarifies what an action means on this page
}

export interface PermissionModule {
  label: string;
  pages: PermissionPage[];
}

const CRUD: PermissionOperation[] = ["view", "add", "edit", "remove"];

export const PERMISSION_MODULES: PermissionModule[] = [
  {
    label: "Dashboard",
    pages: [{ key: "analytics", label: "Analytics", path: "/analytics", actions: ["view"] }],
  },
  {
    label: "Contacts",
    pages: [
      { key: "cases", label: "Cases", path: "/case/list", actions: CRUD },
      { key: "doctors", label: "Doctors", path: "/doctor/list", actions: CRUD },
      { key: "customers", label: "Customers", path: "/customer/list", actions: CRUD },
      { key: "vendors", label: "Vendors", path: "/vendor/list", actions: CRUD },
    ],
  },
  {
    label: "Transactions",
    pages: [
      { key: "bookings", label: "Bookings", path: "/booking/list", actions: ["view", "edit", "exports"], hint: "Edit = cancel booking" },
      { key: "wallets", label: "Wallets", path: "/wallet/list", actions: ["view", "exports"] },
      { key: "withdrawals", label: "Withdrawals", path: "/withdrawal/list", actions: ["view", "edit", "exports"], hint: "Edit = approve / reject" },
    ],
  },
  {
    label: "Masters",
    pages: [
      { key: "users", label: "Users", path: "/user/list", actions: CRUD },
      { key: "specialization", label: "Specialization", path: "/specialization/list", actions: CRUD },
      { key: "qualification", label: "Qualification", path: "/qualification/list", actions: CRUD },
      { key: "symptoms", label: "Symptoms", path: "/symptoms/list", actions: CRUD },
      { key: "brand", label: "Brand", path: "/brand/list", actions: CRUD },
      { key: "doctorService", label: "Doctor Service", path: "/doctor-service/list", actions: CRUD },
      { key: "vendorType", label: "Vendor Type", path: "/vendor-type/list", actions: CRUD },
      { key: "medicalCategory", label: "Medical Category", path: "/medical-category/list", actions: CRUD },
    ],
  },
  {
    label: "Settings",
    pages: [{ key: "roles", label: "Roles & Permissions", path: "/role/list", actions: CRUD }],
  },
];

export const ALL_PERMISSION_PAGES: PermissionPage[] = PERMISSION_MODULES.flatMap((m) => m.pages);

/** Page key whose list route is `path` (e.g. "/doctor/list" -> "doctors"), if any. */
export const pageKeyForPath = (path: string | undefined): string | undefined =>
  path ? ALL_PERMISSION_PAGES.find((p) => p.path === path)?.key : undefined;

/** page key -> operation -> granted */
export type PermissionMatrixValue = Record<string, Partial<Record<PermissionOperation, boolean>>>;

/** Shape exchanged with the API; same shape getPermissions() reads. */
export interface ApiPagePermission {
  page: string;
  actions: { operation: PermissionOperation; permission: boolean }[];
}

export const emptyMatrix = (): PermissionMatrixValue =>
  Object.fromEntries(
    ALL_PERMISSION_PAGES.map((p) => [p.key, Object.fromEntries(p.actions.map((a) => [a, false]))])
  );

export const toApiPermissions = (matrix: PermissionMatrixValue): ApiPagePermission[] =>
  ALL_PERMISSION_PAGES.map((p) => ({
    page: p.key,
    actions: p.actions.map((operation) => ({ operation, permission: !!matrix[p.key]?.[operation] })),
  }));

/** Builds a matrix from API data; unknown pages/operations are ignored, missing ones default to false. */
export const fromApiPermissions = (permissions: ApiPagePermission[] | undefined): PermissionMatrixValue => {
  const matrix = emptyMatrix();
  (permissions || []).forEach((item) => {
    const page = ALL_PERMISSION_PAGES.find((p) => p.key.toLowerCase() === item?.page?.toLowerCase());
    if (!page) return;
    (item.actions || []).forEach((action) => {
      const op = action?.operation?.toLowerCase() as PermissionOperation;
      if (page.actions.includes(op)) matrix[page.key][op] = !!action.permission;
    });
  });
  return matrix;
};
